import React, { useRef, useEffect } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Divider } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import TitleIcon from '@mui/icons-material/Title';

/**
 * 依存追加なしの軽量リッチテキストエディタ。
 * contentEditable + document.execCommand で太字/斜体/下線/リスト/見出しを編集し、HTMLを扱う。
 * value は制御されたHTML文字列。変更時に onChange(html) を呼ぶ。
 */
interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, minHeight = 180 }) => {
  const editorRef = useRef<HTMLDivElement>(null);

  // 外部からの value 変更をエディタに反映（カーソル移動を避けるため差分時のみ）
  useEffect(() => {
    const el = editorRef.current;
    if (el && el.innerHTML !== value) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emitChange = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // ツールバーのボタン押下で書式を適用（フォーカスを保ってから実行）
  const exec = (command: string, arg?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emitChange();
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexWrap: 'wrap',
        }}
      >
        <ToggleButtonGroup size="small">
          <ToggleButton value="bold" onClick={() => exec('bold')} title="太字">
            <FormatBoldIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="italic" onClick={() => exec('italic')} title="斜体">
            <FormatItalicIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="underline" onClick={() => exec('underline')} title="下線">
            <FormatUnderlinedIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        <ToggleButtonGroup size="small">
          <ToggleButton value="h3" onClick={() => exec('formatBlock', 'H3')} title="見出し">
            <TitleIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="ul" onClick={() => exec('insertUnorderedList')} title="箇条書き">
            <FormatListBulletedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="ol" onClick={() => exec('insertOrderedList')} title="番号付きリスト">
            <FormatListNumberedIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        sx={{
          minHeight,
          p: 1.5,
          outline: 'none',
          '& ul, & ol': { pl: 3, my: 1 },
          '& h3': { my: 1 },
          '& p': { my: 0.5 },
          fontSize: 14,
          lineHeight: 1.6,
        }}
      />
    </Box>
  );
};

export default RichTextEditor;
